import { Component, ComponentClass } from './ComponentBase';
import { componentRegistry } from './ComponentRegistry';

/**
 * Helper for simple tag and data components that don't need custom serialization.
 * Automatically generates equals() and getTestInstances() methods.
 *
 * Use this for:
 * - Tag components (empty marker classes)
 * - Data components with simple properties (no nested objects, no HexCoords, etc.)
 *
 * The base Component constructor handles deserialization via Object.assign,
 * so no custom constructor is needed for simple components.
 *
 * @param ComponentClass - The component class constructor
 * @param testInstances - Optional array of test instances for edge case testing
 * @returns The registered component class with auto-generated methods
 *
 * @example
 * ```typescript
 * // Tag component
 * class Dead extends Component {}
 * export default simpleComponent(Dead);
 *
 * // Data component with default property values
 * class Faction extends Component {
 *   name: string = '';
 * }
 * export default simpleComponent(Faction, [
 *   new Faction({ name: 'player' }),
 *   new Faction({ name: 'enemy' }),
 *   new Faction(), // edge case: default empty name
 * ]);
 * ```
 */
export function simpleComponent<T extends Component>(
  ComponentClass: new (...args: any[]) => T,
  testInstances?: T[]
): ComponentClass<T> {
  // Auto-generate equals() using JSON comparison
  // Works for components with simple serializable properties
  (ComponentClass.prototype as any).equals = function(other: T): boolean {
    return JSON.stringify(this.toJSON()) === JSON.stringify(other.toJSON());
  };

  // Auto-generate getTestInstances()
  // Uses provided test instances or creates a single instance with no-arg constructor
  (ComponentClass as any).getTestInstances = function(): T[] {
    if (testInstances) {
      return testInstances;
    }
    // Default: create one instance using no-arg constructor
    return [new ComponentClass()];
  };

  // Register the component and return it
  return componentRegistry.register(ComponentClass as ComponentClass<T>);
}

/**
 * Helper for components with custom constructor/serialization logic.
 * Registers the component and validates that equals and getTestInstances are properly implemented.
 *
 * Use this for components that:
 * - Have complex deserialization (HexCoords.get(), nested class instances)
 * - Need custom toJSON() logic
 * - Have validation logic in the constructor
 * - Need custom equals() logic (for nested object comparison)
 *
 * Components using this helper MUST manually implement:
 * - constructor(data?: {...}) that handles deserialization
 * - equals(other: this): boolean
 * - static getTestInstances(): T[]
 *
 * @param ComponentClass - The component class with custom methods
 * @returns The registered component class
 *
 * @example
 * ```typescript
 * class HexPosition extends Component {
 *   coords: HexCoords;
 *   bearing: number;
 *
 *   constructor(data: { coords?: {q: number, r: number}, bearing?: number } = {}) {
 *     super();
 *     this.coords = HexCoords.get(data.coords ?? { q: 0, r: 0 });
 *     this.bearing = data.bearing ?? 1;
 *   }
 *
 *   equals(other: this): boolean {
 *     return this.coords.equals(other.coords) &&
 *            this.bearing === other.bearing;
 *   }
 *
 *   static getTestInstances(): HexPosition[] {
 *     return [
 *       new HexPosition({ coords: { q: 0, r: 0 }, bearing: 1 }),
 *       new HexPosition({ coords: { q: 5, r: -3 }, bearing: 6 }),
 *     ];
 *   }
 * }
 * export default registerComponent(HexPosition);
 * ```
 */
export function registerComponent<T extends Component>(
  ComponentClass: ComponentClass<T>
): ComponentClass<T> {
  return componentRegistry.register(ComponentClass);
}
